/**
 * ColumnFilterHeader — generic sortable + filterable column header.
 *
 * Renders the existing sort toggle (via `dataTableSortHeader`) plus a funnel
 * (`ListFilter`) icon Button. When the column has an active filter the funnel is
 * `selected` (distinct resting container) with a brand-colored dot. Clicking it
 * opens a portal `Popover` anchored to the button, hosting the type-appropriate
 * `ColumnFilterPopover` control.
 *
 * Drop-in: use `columnFilterHeader(spec, labels)` as a column def `header`. Any
 * column with a `ColumnFilterSpec` gets header sorting + filtering for free.
 */
import { useRef, useState } from "react";
import type { HeaderContext } from "@tanstack/react-table";
import { ListFilter } from "lucide-react";
import { Popover } from "@/components/ui/Popover";
import { Button } from "@/components/ui/atoms/Button";
import { dataTableSortHeader } from "../DataTable/DataTable.sortHeader";
import type { DataTableSortHeaderOptions } from "../DataTable/DataTable.types";
import { ColumnFilterPopover } from "./ColumnFilterPopover";
import type { ColumnFilterPopoverLabels } from "./ColumnFilterPopover";
import { isColumnFilterEmpty } from "./columnFilter.filterFns";
import type {
  ColumnFilterSpec,
  ColumnFilterValue,
} from "./columnFilter.types";

export interface ColumnFilterHeaderLabels extends ColumnFilterPopoverLabels {
  /** aria-label for the funnel toggle (e.g. "Filter {{column}}"). */
  filterAria: string;
}

interface ColumnFilterHeaderCellProps<TData, TValue> {
  ctx: HeaderContext<TData, TValue>;
  spec: ColumnFilterSpec;
  labels: ColumnFilterHeaderLabels;
  sortOptions?: DataTableSortHeaderOptions;
}

function ColumnFilterHeaderCell<TData, TValue>({
  ctx,
  spec,
  labels,
  sortOptions,
}: ColumnFilterHeaderCellProps<TData, TValue>) {
  const { column } = ctx;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  const SortHeader = dataTableSortHeader<TData, TValue>(spec.label, sortOptions);

  const rawValue = column.getFilterValue() as ColumnFilterValue | undefined;
  const active = !isColumnFilterEmpty(spec, rawValue);

  return (
    <div className="flex items-center gap-1">
      {SortHeader(ctx)}
      <span className="relative inline-flex">
        <Button
          ref={anchorRef}
          type="icon"
          intent="muted"
          size="xs"
          selected={active}
          label={labels.filterAria}
          aria-label={labels.filterAria}
          icon={<ListFilter strokeWidth={active ? 2.5 : 2}/>
          }
          className={active ? "text-purple" : undefined}
          onClick={() => setOpen((o) => !o)}
        />
        {active && (
          <span
            className="pointer-events-none absolute -right-0.5 -top-0.5 z-[3] h-1.5 w-1.5 rounded-full bg-purple ring-1 ring-light"
            aria-hidden
          />
        )}
      </span>

      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        contentClassName="bg-white border border-light-lila/60 rounded-lg shadow-lg"
      >
        <ColumnFilterPopover
          spec={spec}
          value={rawValue}
          labels={labels}
          onChange={(next) => column.setFilterValue(next)}
          onClear={() => {
            column.setFilterValue(undefined);
            setOpen(false);
          }}
        />
      </Popover>
    </div>
  );
}

/**
 * Build a column-def `header` that renders the sort toggle + filter funnel popover.
 * @example header: columnFilterHeader(skillsSpec, labels)
 */
export function columnFilterHeader<TData, TValue = unknown>(
  spec: ColumnFilterSpec,
  labels: ColumnFilterHeaderLabels,
  sortOptions?: DataTableSortHeaderOptions
) {
  return function Header(ctx: HeaderContext<TData, TValue>) {
    return (
      <ColumnFilterHeaderCell
        ctx={ctx}
        spec={spec}
        labels={labels}
        sortOptions={sortOptions}
      />
    );
  };
}
